import { HttpContextToken, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { retry, shareReplay, tap } from 'rxjs/operators';
import { CacheService } from '../../core/services/cache.service';
import { inject } from '@angular/core';

const STATIC_ASSET_TTL = 24 * 60 * 60 * 1000; // 24 hours
const DEFAULT_TTL = 1000; // 1 second

export const SKIP_HTTP_CACHE = new HttpContextToken<boolean>(() => false);

function isStaticAsset(url: string): boolean {
  return url.includes('/assets/') || url.startsWith('assets/');
}

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const CachingInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const cacheService = inject(CacheService);
  if (req.method !== 'GET') {
    cacheService.clearCache();
    return next(req);
  }

  if (req.context.get(SKIP_HTTP_CACHE)) {
    return next(req);
  }

  const cachedResponse = cacheService.cache.get(req.urlWithParams);
  if (cachedResponse && cachedResponse.expiry > Date.now()) {
    return of(cachedResponse.response);
  }

  const ttl = isStaticAsset(req.urlWithParams) ? STATIC_ASSET_TTL : DEFAULT_TTL;

  let response$ = next(req).pipe(
    tap(event => {
      if (event instanceof HttpResponse) {
        cacheService.cache.set(req.urlWithParams, {
          response: event,
          expiry: Date.now() + ttl,
        });
      }
    }),
    shareReplay(1)
  );

  if (isStaticAsset(req.urlWithParams)) {
    response$ = response$.pipe(
      retry({ count: 2, delay: 500 })
    );
  }

  return response$;
};
